import type { Maybe } from '@takeshape/util';
import clone from 'lodash/clone';
import set from 'lodash/fp/set';
import get from 'lodash/get';
import omit from 'lodash/omit';
import setWith from 'lodash/setWith';
import updateWith from 'lodash/updateWith';
import { type ActionType, createReducer } from 'typesafe-actions';
import { logoutUser } from '../auth/actions.js';
import { pickAvatarColor } from '../common/colors.js';
import { contentLocked, contentUnlocked, lockBroken, lockContent, unlockContent } from '../content/actions.js';
import {
  type Actions as CollabActions,
  getCollabUsers,
  initUsers,
  userEntered,
  userLeft,
  userMovedFromContent,
  userMovedToContent
} from './actions';
import type { CollabState, ContentUsers, UserId } from './types.js';

export const initialState: CollabState = {
  projectUsers: {},
  contentUsers: {}
};

function getPath(contentId?: string, userId?: UserId): string[] {
  const path = contentId ? ['contentUsers', contentId] : ['projectUsers'];
  if (userId) {
    path.push(userId);
  }

  return path;
}

const lockUserPath = (contentId: string) => getPath(contentId).concat('lockUser');

const viewingUsersPath = (contentId: string) => getPath(contentId).concat('viewingUsers');

const setLock = (state: CollabState, contentId: string, userId: UserId) =>
  setWith(clone(state), lockUserPath(contentId), userId, clone);

const removeLock = (state: CollabState, contentId: string) => setWith(state, lockUserPath(contentId), undefined, clone);

const addToViewing = (state: CollabState, contentId: string, userId?: UserId) =>
  updateWith(
    clone(state),
    viewingUsersPath(contentId),
    (viewingUsers?: Set<UserId>) => {
      const users = new Set<UserId>(viewingUsers ? [...viewingUsers] : []);
      if (userId) users.add(userId);
      return users;
    },
    clone
  );

const removeFromViewing = (state: CollabState, contentId: string, userId?: UserId) =>
  updateWith(
    clone(state),
    viewingUsersPath(contentId),
    (viewingUsers?: Set<UserId>) => {
      const users = new Set<UserId>(viewingUsers ? [...viewingUsers] : []);
      if (userId) users.delete(userId);
      return users;
    },
    clone
  );

const setLockAsUser = (state: CollabState, contentId: string, userId: UserId) => {
  state = setLock(state, contentId, userId);
  state = removeFromViewing(state, contentId, userId);
  return state;
};

const addUserToViewing = (state: CollabState, contentId: string, userId?: UserId) => {
  const userHoldingLock = get(state, lockUserPath(contentId));
  if (userHoldingLock !== userId) {
    state = addToViewing(state, contentId, userId);
  }

  return state;
};

type Actions =
  | CollabActions
  | ActionType<typeof logoutUser>
  | ActionType<typeof lockContent.fulfilled>
  | ActionType<typeof unlockContent.fulfilled>
  | ActionType<typeof contentLocked>
  | ActionType<typeof contentUnlocked>
  | ActionType<typeof lockBroken>;

export default createReducer<CollabState, Actions>(initialState)
  .handleAction(initUsers, (state, action) => {
    const {
      payload: {
        users,
        channelInfo: { contentId }
      }
    } = action;
    if (contentId) {
      const lockUserId = get(state, lockUserPath(contentId));
      const userIds = Object.values(users)
        .map(({ id }) => id !== lockUserId && id)
        .filter(Boolean);
      return setWith(clone(state), viewingUsersPath(contentId), new Set(userIds), clone);
    }

    for (const user of Object.values(users)) {
      user.color = pickAvatarColor(user.id);
    }
    return setWith(clone(state), getPath(contentId), users, clone);
  })

  .handleAction(userEntered, (state, action) => {
    const {
      payload: {
        user,
        channelInfo: { contentId }
      }
    } = action;
    if (contentId) {
      return addUserToViewing(state, contentId, user.id);
    }

    user.color = pickAvatarColor(user.id);
    return setWith(clone(state), getPath(contentId, user.id), user, clone);
  })

  .handleAction(userLeft, (state, action) => {
    const {
      payload: {
        user: { id: userId },
        channelInfo: { contentId }
      }
    } = action;
    if (contentId) {
      if (get(state, lockUserPath(contentId)) === userId) {
        return removeLock(state, contentId);
      }

      return removeFromViewing(state, contentId, userId);
    }

    const path = getPath(contentId);
    return set(path, omit(get(state, path), userId), state);
  })

  .handleAction(lockContent.fulfilled, (state, action) => {
    const {
      payload,
      meta: { contentId }
    } = action;
    const userId = get(payload, ['lock', 'user', 'id']);
    return setLockAsUser(state, contentId, userId);
  })

  .handleAction(unlockContent.fulfilled, (state, action) => {
    const {
      meta: { contentId }
    } = action;
    return removeLock(state, contentId);
  })

  // The remaining actions do not add or remove users from the state, they only modify
  // existing ones. This makes the builtIn pusher events above the single source of truth
  // for whether users are in the project.

  .handleAction(getCollabUsers.fulfilled, (state, action) => {
    const {
      payload: { members }
    } = action;
    const { projectUsers } = state;
    const projectMembers = members.filter(({ userId }) => projectUsers[userId] !== undefined);
    const contentUsers: Record<string, ContentUsers> = {};
    for (const { userId, contentList } of projectMembers) {
      for (const { contentId, hasLock } of contentList) {
        const users = contentUsers[contentId] || {};
        if (hasLock) {
          users.lockUser = userId;
        } else {
          users.viewingUsers = new Set<UserId>(users.viewingUsers).add(userId);
        }

        contentUsers[contentId] = users;
      }
    }
    return setWith(clone(state), 'contentUsers', contentUsers, clone);
  })

  .handleAction(userMovedToContent, (state, action) => {
    const {
      payload: { contentId, userId, hasLock }
    } = action;
    if (get(state, ['projectUsers', userId])) {
      state = (hasLock ? setLockAsUser : addUserToViewing)(state, contentId, userId);
    }

    return state;
  })

  .handleAction(userMovedFromContent, (state, action) => {
    const {
      payload: { contentId, userId }
    } = action;
    return get(state, lockUserPath(contentId)) === userId
      ? removeLock(state, contentId)
      : removeFromViewing(state, contentId, userId);
  })

  .handleAction(contentLocked, (state, { payload }) => {
    const userId = get(payload, ['lock', 'user', 'id']);
    const { contentId } = payload;
    if (userId && contentId && get(state, ['projectUsers', userId])) {
      const prevLockUser: Maybe<UserId> = get(state, lockUserPath(contentId));
      state = setLockAsUser(state, contentId, userId);
      state = addUserToViewing(state, contentId, prevLockUser);
    }

    return state;
  })

  .handleAction(contentUnlocked, (state, { payload }) => {
    const { contentId } = payload;
    if (contentId) {
      const prevLockUser: Maybe<UserId> = get(state, lockUserPath(contentId));
      state = removeLock(state, contentId);
      state = addToViewing(state, contentId, prevLockUser);
    }

    return state;
  })

  .handleAction(lockBroken, (state, { payload: { contentId } }) => {
    const prevLockUser: Maybe<UserId> = get(state, lockUserPath(contentId));
    state = removeLock(state, contentId);
    return addToViewing(state, contentId, prevLockUser);
  })

  .handleAction(logoutUser.fulfilled, () => initialState);
